import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SearchInput from '../../components/base/SearchInput';
import FilterTabs from '../../components/base/FilterTabs';
import GuidelineCard from '../../components/base/GuidelineCard';
import { mockAppraisalGuidelines } from '../../mocks/appraisal-guidelines';

export default function AppraisalGuidelinesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [sortBy, setSortBy] = useState('relevance');

  const filteredGuidelines = useMemo(() => {
    let filtered = mockAppraisalGuidelines;

    // Filter by search query
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(guideline =>
        guideline.title.toLowerCase().includes(searchLower) ||
        guideline.description.toLowerCase().includes(searchLower) ||
        guideline.source.toLowerCase().includes(searchLower) ||
        guideline.category.toLowerCase().includes(searchLower) ||
        guideline.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    // Filter by agency
    if (selectedAgency && selectedAgency !== 'all') {
      filtered = filtered.filter(guideline => guideline.source === selectedAgency);
    }

    // Sort guidelines
    if (sortBy === 'date') {
      filtered = [...filtered].sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
    } else if (sortBy === 'agency') {
      filtered = [...filtered].sort((a, b) => a.source.localeCompare(b.source));
    } else if (sortBy === 'relevance' && searchQuery.trim()) {
      filtered = [...filtered].sort((a, b) => {
        const aScore = a.title.toLowerCase().includes(searchQuery.toLowerCase()) ? 2 : 1;
        const bScore = b.title.toLowerCase().includes(searchQuery.toLowerCase()) ? 2 : 1;
        return bScore - aScore;
      });
    }

    return filtered;
  }, [searchQuery, selectedAgency, sortBy]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filter: string) => {
    setSelectedAgency(filter === 'all' ? '' : filter);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedAgency('');
  };

  const getFilterDisplayText = () => {
    const filters = [];
    if (selectedAgency) {
      filters.push(`Agency: ${selectedAgency}`);
    }
    return filters.length > 0 ? ` • ${filters.join(' • ')}` : '';
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <i className="ri-home-line text-3xl text-white"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Appraisal Guidelines
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
            Comprehensive appraisal standards, property valuation requirements, and compliance guidelines for all major agencies
          </p>
          
          {/* Search */}
          <div className="max-w-2xl mx-auto">
            <SearchInput 
              onSearch={handleSearch}
              placeholder="Search appraisal guidelines, property types, valuation methods..."
            />
          </div>
        </div>
      </section>

      {/* Quick Access Cards */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Quick Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <button
              onClick={() => setSearchQuery('property valuation')}
              className="bg-slate-700 border border-slate-600 rounded-xl p-6 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/20 cursor-pointer group"
            >
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-price-tag-3-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Property Valuation</h3>
              <p className="text-slate-400 text-sm">Valuation methods and approaches</p>
            </button>

            <button
              onClick={() => setSearchQuery('comparable sales')}
              className="bg-slate-700 border border-slate-600 rounded-xl p-6 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/20 cursor-pointer group"
            >
              <div className="w-12 h-12 bg-violet-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-bar-chart-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Comparable Sales</h3>
              <p className="text-slate-400 text-sm">Comp selection and analysis</p>
            </button>

            <button
              onClick={() => setSearchQuery('property condition')}
              className="bg-slate-700 border border-slate-600 rounded-xl p-6 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/20 cursor-pointer group"
            >
              <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-tools-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Property Condition</h3>
              <p className="text-slate-400 text-sm">Condition ratings and requirements</p>
            </button>

            <button
              onClick={() => setSearchQuery('appraisal review')}
              className="bg-slate-700 border border-slate-600 rounded-xl p-6 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/20 cursor-pointer group"
            >
              <div className="w-12 h-12 bg-rose-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-search-eye-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Appraisal Review</h3>
              <p className="text-slate-400 text-sm">Review standards and quality control</p>
            </button>
          </div>
        </div>
      </section>

      {/* Guidelines Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                {searchQuery.trim() ? `Search Results for "${searchQuery}"` : 'All Appraisal Guidelines'}
              </h2>
              <p className="text-slate-400">
                {filteredGuidelines.length} guidelines found{getFilterDisplayText()}
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <label className="text-slate-400 text-sm">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-slate-800 text-slate-300 border border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pr-8"
              >
                <option value="relevance">Relevance</option>
                <option value="date">Last Updated</option>
                <option value="agency">Agency</option>
              </select>
            </div>
          </div>

          <FilterTabs 
            activeFilter={selectedAgency || 'all'}
            onFilterChange={handleFilterChange}
          />

          {filteredGuidelines.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-slate-800 rounded-full">
                <i className="ri-search-line text-3xl text-slate-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-300 mb-2">No appraisal guidelines found</h3>
              <p className="text-slate-500 mb-6">
                Try adjusting your search terms or filters to find what you're looking for.
              </p>
              <button 
                onClick={clearAllFilters}
                className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-500 font-medium cursor-pointer whitespace-nowrap transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGuidelines.map((guideline) => (
                <GuidelineCard key={guideline.id} guideline={guideline} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}